import { all, put, select, takeEvery } from '@redux-saga/core/effects';
import { api, CancelToken } from 'api';
import { ActionType } from 'typesafe-actions';
import { call, cancel, cancelled, fork, take } from 'redux-saga/effects';
import log from 'loglevel';
import { EffectR } from 'types/utils';
import { Task } from 'redux-saga';
import { getRegionCode } from 'common/selectors';
import {
  PERFORM_UPLOAD_PHOTO,
  PERFORM_UPLOAD_RETRY,
  PROCESS_SELECTED_PHOTOS,
  PUT_UPLOAD_PHOTO_RECORD,
  REMOVE_SELECTED_PHOTO,
  STOP_UPLOAD,
  UploadPhotoCache,
  UploadPhotoError,
} from '../actions/upload-photos';
import { getUploadCacheByToken } from '../selectors/upload-photos';
import { UploadPhotoToken } from '../types';
import { postPhoto } from '../utils/api';
import { buildObjectUrl, buildUploadToken } from '../utils/upload-photos';

export function* processSelectedPhotos(action: ActionType<typeof PROCESS_SELECTED_PHOTOS>) {
  const photos = action.payload;

  const uploadTasks = photos.map((file) => {
    const src = buildObjectUrl(file);
    const uploadToken = buildUploadToken();

    return all([
      put(
        PUT_UPLOAD_PHOTO_RECORD({
          file,
          uploadState: {
            coverSrc: src,
            isInProcessing: false,
            isUploaded: false,
            uploadedPhotoId: null,
            processingIsFailed: false,
            uploadToken,
          },
        }),
      ),
      put(
        PERFORM_UPLOAD_PHOTO.request({
          file,
          uploadToken,
        }),
      ),
    ]);
  });

  yield all(uploadTasks);
}

export function* performPhotoUpload(action: ActionType<typeof PERFORM_UPLOAD_PHOTO.request>) {
  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;
  const { file, uploadToken } = action.payload;
  const uploadTask = (yield fork(doPhotoUpload, file, uploadToken, abortController.token)) as Task;
  yield call(takeStopByToken, uploadToken);
  abortController.cancel();
  yield cancel(uploadTask);
}

export function* takeStopByToken(uploadToken: UploadPhotoToken) {
  while (true) {
    const action: ActionType<typeof STOP_UPLOAD> = yield take(STOP_UPLOAD);
    if (action.payload === uploadToken) {
      break;
    }
  }
}

export function* performRemovePhoto(action: ActionType<typeof REMOVE_SELECTED_PHOTO>) {
  yield put(STOP_UPLOAD(action.payload));
}

export function* doPhotoUpload(file: File, uploadToken: UploadPhotoToken, cancelToken?: CancelToken) {
  try {
    const regionCode = (yield select(getRegionCode)) as ReturnType<typeof getRegionCode>;
    const photoId = (yield call(postPhoto, file, cancelToken, regionCode)) as EffectR.Call<typeof postPhoto>;
    yield put(PERFORM_UPLOAD_PHOTO.success({ uploadToken, photoId }));
  } catch (err) {
    log.error('Saga doPhotoUpload error: ', err);

    if (err instanceof Error) {
      yield put(PERFORM_UPLOAD_PHOTO.failure(new UploadPhotoError(uploadToken, err.message)));
    }
  } finally {
    if ((yield cancelled()) as boolean) {
      yield put(PERFORM_UPLOAD_PHOTO.cancel(uploadToken));
    }
  }
}

export function* doRetryUpload(action: ActionType<typeof PERFORM_UPLOAD_RETRY>) {
  const uploadToken = action.payload;
  const uploadCache: UploadPhotoCache = yield select(getUploadCacheByToken(uploadToken));
  if (uploadCache) {
    yield put(
      PERFORM_UPLOAD_PHOTO.request({
        file: uploadCache.file,
        uploadToken,
      }),
    );
  }
}

export function* photoUploadSagas() {
  yield takeEvery(PROCESS_SELECTED_PHOTOS, processSelectedPhotos);
  yield takeEvery(PERFORM_UPLOAD_PHOTO.request, performPhotoUpload);
  yield takeEvery(PERFORM_UPLOAD_RETRY, doRetryUpload);
  yield takeEvery(REMOVE_SELECTED_PHOTO, performRemovePhoto);
}
